import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PlusIcon, TrashIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import type { EmailReceiver, EmailAttachments } from '../types/invoice.types';
import { showWarning } from '@/utils/sweetAlert';
import { ModalFooterHint, ModalSectionBlock } from '@components/ui';
import {
  CTA,
  FIELD,
  GHOST,
  ICON_BTN_DANGER,
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CLOSE_X,
  MODAL_FOOT,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';

interface ResendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (attachments: EmailAttachments, receivers: EmailReceiver[]) => void;
  isSubmitting?: boolean;
}

export function ResendEmailModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ResendEmailModalProps) {
  const [attachments, setAttachments] = useState<EmailAttachments>({
    dResponseAuthXML: false,
    dResponseXML: false,
    dCFe: true,
    dCAFE: true,
  });

  const [receivers, setReceivers] = useState<EmailReceiver[]>([
    { Email: '', Name: '' },
  ]);

  const handleAttachmentChange = (key: keyof EmailAttachments) => {
    setAttachments((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleReceiverChange = (index: number, field: keyof EmailReceiver, value: string) => {
    setReceivers((prev) => {
      const newReceivers = [...prev];
      newReceivers[index] = {
        ...newReceivers[index],
        [field]: value,
      };
      return newReceivers;
    });
  };

  const addReceiver = () => {
    setReceivers((prev) => [...prev, { Email: '', Name: '' }]);
  };

  const removeReceiver = (index: number) => {
    if (receivers.length > 1) {
      setReceivers((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate at least one attachment is selected
    const hasAttachment = Object.values(attachments).some((v) => v);
    if (!hasAttachment) {
      showWarning('Adjuntos Requeridos', 'Debe seleccionar al menos un documento adjunto');
      return;
    }

    // Validate all receivers have email
    const validReceivers = receivers.filter((r) => r.Email.trim() !== '');
    if (validReceivers.length === 0) {
      showWarning('Destinatario Requerido', 'Debe agregar al menos un destinatario con email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validReceivers.filter((r) => !emailRegex.test(r.Email));
    if (invalidEmails.length > 0) {
      showWarning('Email Inválido', 'Por favor ingrese emails válidos');
      return;
    }

    // Set name same as email if empty
    const finalReceivers = validReceivers.map((r) => ({
      Email: r.Email,
      Name: r.Name.trim() || r.Email,
    }));

    onSubmit(attachments, finalReceivers);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={MODAL_BACKDROP} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid="invoice-resend-email-modal"
                className={cn('flex max-h-[calc(100vh-2rem)] max-w-2xl flex-col', MODAL_PANEL)}
              >
                {/* ---------- HEAD ---------- */}
                <div className={cn(MODAL_HEAD, 'shrink-0')}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3.5">
                      <span className={MODAL_ICON_TILE}>
                        <EnvelopeIcon />
                      </span>
                      <div className="min-w-0">
                        <div className={MODAL_KICKER}>Factura · Envío</div>
                        <Dialog.Title as="h3" className={cn(MODAL_TITLE, 'mt-1')}>
                          Reenviar Factura por Email
                        </Dialog.Title>
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Cerrar"
                      data-testid="invoice-resend-email-close-button"
                      className={MODAL_CLOSE_X}
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} data-testid="invoice-resend-email-form" className="flex min-h-0 flex-1 flex-col">
                  {/* ---------- BODY ---------- */}
                  <div className={cn(MODAL_BODY, 'min-h-0 flex-1')}>
                    {/* Attachments Section */}
                    <ModalSectionBlock index="01" title="Documentos a Adjuntar" divider={false}>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {/*
                          El testId va por entrada: el diseño unifico los cuatro
                          checkboxes en un .map(), pero la automatizacion de QA
                          los busca uno por uno con el nombre que ya tenian.
                        */}
                        {[
                          { key: 'dCFe' as const, label: 'CFE (Comprobante Fiscal Electrónico)', testId: 'invoice-resend-email-cfe-checkbox' },
                          { key: 'dCAFE' as const, label: 'CAFE (Código de Autorización)', testId: 'invoice-resend-email-cafe-checkbox' },
                          { key: 'dResponseXML' as const, label: 'Response XML', testId: 'invoice-resend-email-response-xml-checkbox' },
                          { key: 'dResponseAuthXML' as const, label: 'Response Auth XML', testId: 'invoice-resend-email-response-auth-xml-checkbox' },
                        ].map(({ key, label, testId }) => (
                          <label
                            key={key}
                            className={cn(
                              'flex cursor-pointer items-center gap-3 rounded-[10px] border px-3.5 py-3 text-[14px] transition-colors',
                              attachments[key]
                                ? 'border-teal-500/30 bg-teal-500/[.06] text-ink-850 dark:text-white'
                                : 'border-[#ececec] bg-white text-secondary-500 hover:border-secondary-300 dark:border-secondary-600 dark:bg-secondary-800'
                            )}
                          >
                            <input
                              type="checkbox"
                              data-testid={testId}
                              checked={attachments[key]}
                              onChange={() => handleAttachmentChange(key)}
                              className="h-[18px] w-[18px] rounded-[6px] border-secondary-300 text-[#0d9488] focus:ring-teal-500/40"
                              disabled={isSubmitting}
                            />
                            <span className="font-medium">{label}</span>
                          </label>
                        ))}
                      </div>
                    </ModalSectionBlock>

                    {/* Receivers Section */}
                    <ModalSectionBlock
                      index="02"
                      title="Destinatarios"
                      aside={
                        <span className="font-mono text-[11px] font-bold tracking-[0.14em] text-secondary-400">
                          {receivers.length}
                        </span>
                      }
                    >
                      <div className="mb-3 flex justify-end">
                        <button
                          type="button"
                          onClick={addReceiver}
                          data-testid="invoice-resend-email-add-receiver-button"
                          className={cn(GHOST, 'px-3 py-2 text-xs')}
                          disabled={isSubmitting}
                        >
                          <PlusIcon className="h-3.5 w-3.5" />
                          Agregar
                        </button>
                      </div>

                      <div className="space-y-2.5">
                        {receivers.map((receiver, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="email"
                              placeholder="Email"
                              data-testid={`invoice-resend-email-receiver-email-input-${index}`}
                              value={receiver.Email}
                              onChange={(e) =>
                                handleReceiverChange(index, 'Email', e.target.value)
                              }
                              className={cn(FIELD, 'flex-1')}
                              disabled={isSubmitting}
                              required
                            />
                            <input
                              type="text"
                              placeholder="Nombre (opcional)"
                              value={receiver.Name}
                              onChange={(e) =>
                                handleReceiverChange(index, 'Name', e.target.value)
                              }
                              className={cn(FIELD, 'flex-1')}
                              disabled={isSubmitting}
                             data-testid={`invoice-resend-email-receiver-name-input-${index}`}/>
                            {receivers.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeReceiver(index)}
                                data-testid={`invoice-resend-email-remove-receiver-button-${index}`}
                                aria-label="Quitar destinatario"
                                className={ICON_BTN_DANGER}
                                disabled={isSubmitting}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </ModalSectionBlock>
                  </div>

                  {/* ---------- FOOT ---------- */}
                  <div className={cn(MODAL_FOOT, 'shrink-0')}>
                    <ModalFooterHint>
                      Se enviará a {receivers.length} destinatario{receivers.length !== 1 ? 's' : ''}
                    </ModalFooterHint>
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={handleClose}
                        className={GHOST}
                        disabled={isSubmitting}
                        data-testid="invoice-resend-email-cancel-button"
                      >
                        Cancelar
                      </button>
                      <button type="submit" className={CTA} disabled={isSubmitting} data-testid="invoice-resend-email-submit-button">
                        {isSubmitting ? 'Enviando...' : 'Enviar Email'}
                      </button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
